// @ts-nocheck
import { useCallback, useEffect, useMemo } from "react";

import { useMachine } from "@xstate/react";
import { ActionTypes } from "xstate";

import { type Context, type Events, createInputStateMachine } from "./InputStateMachine";

export interface UseInputStateApi<InputType> {
    inputProps: { value: InputType; onChange: (newValue: InputType) => void; error: string | undefined };
    isValid: boolean;
    isTouched: boolean;
    reset: () => void;
}

interface InputStateOptions<InputType> {
    defaultValue: InputType;
    error?: string | ((value: InputType) => string | null);
    validator?: (value: InputType) => boolean;
    onChanged?: (value: InputType, isValid: boolean) => void;
    showErrorByDefault?: boolean;
}

const defaultValidator = <InputType>(value: InputType) => true;

const createErrorMessage = <InputType>(value: InputType, error: string | ((value: InputType) => string)) => {
    return error instanceof Function ? error(value) : error;
};

export const useInputState = <InputType>({
    defaultValue,
    validator,
    onChanged,
    error,
    showErrorByDefault = false,
}: InputStateOptions<InputType>): UseInputStateApi<InputType> => {
    const [state, sendToMachine, service] = useMachine<Context<InputType>, Events<InputType>>(
        createInputStateMachine<InputType>(defaultValue, validator ?? defaultValidator),
    );

    const {
        context: { value, isTouched },
    } = state;

    const isValid = state.value === "valid";

    const showError = !isValid && (isTouched || showErrorByDefault);

    useEffect(() => {
        const subscription = service.subscribe((newState) => {
            if (onChanged && newState.event.type !== ActionTypes.Init && newState.context.value !== value) {
                onChanged(newState.context.value, !newState.matches("error"));
            }
        });
        return () => {
            subscription.unsubscribe();
        };
    }, [service, onChanged, value]);

    const onChange = useCallback(
        (newValue: InputType) => {
            sendToMachine({ type: "CHANGED", newValue });
        },
        [sendToMachine],
    );

    const reset = useCallback(() => {
        sendToMachine({ type: "RESET" });
    }, [sendToMachine]);

    const inputProps = useMemo(
        () => ({
            value,
            onChange,
            error: showError && error !== null ? createErrorMessage(value, error as string) : undefined,
        }),
        [value, onChange, showError, error],
    );

    return { inputProps, isValid, isTouched, reset };
};
